import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import {
  emailChangeSchema,
  shownFieldError,
} from "../../../../utils/validationPatterns";
import useAuth from "../../../../hooks/useAuth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { useGetEntityUser } from "../../../../hooks/users";
import {
  useResendValidationEmail,
  useUpdateEmail,
} from "../../../../hooks/entity";
import { Timer } from "../../../../utils/Timer";

const ProfileEmailChange = () => {
  const [t] = useTranslation();
  const { customer_identification, user } = useAuth();

  const [show, setShow] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [isResendRequest, setIsResendRequest] = useState(false);

  const { data, isSuccess } = useGetEntityUser({
    customer_identification: customer_identification,
    entityIdentification: user.entity_identification,
    user_identification: customer_identification,
  });

  const { mutate: updateEmail, isLoading } = useUpdateEmail();
  const { mutate: resendValidationEmail, isLoading: isResending } =
    useResendValidationEmail();
  const {
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    trigger,
    setValue,
    reset,
    control,
  } = useForm({
    resolver: joiResolver(emailChangeSchema),
    defaultValues: {
      user_email: "",
      new_user_email: "",
      new_user_email_confirmation: "",
    },
  });

  const handleResendValidationLink = () => {
    if (!isValid) return trigger();

    setIsResendRequest(true);

    let newEmailInfos = getValues();

    if (
      newEmailInfos.new_user_email &&
      newEmailInfos.new_user_email_confirmation
    ) {
      updateEmail({
        customer_identification: customer_identification,
        entity_identification: user.entity_identification,
        data: getValues(),
      });
    } else {
      if (isSuccess) {
        setShowTimer(false);
        resendValidationEmail(
          {
            customer_identification: customer_identification,
            entity_identification: user.entity_identification,
          },
          {
            onSuccess: () => {
              setShowTimer(true);
              setIsResendRequest(false);
            },
          }
        );
      }
    }
  };

  const onSubmit = (values: any) => {
    updateEmail({
      customer_identification: customer_identification,
      entity_identification: user.entity_identification,
      data: values,
    });
  };

  useEffect(() => {
    if (isSuccess && data?.user_email) reset({ user_email: data?.user_email });
  }, [data, isSuccess, reset, setValue]);

  return (
    <div className="px-5 pt-5 pb-8 bg-white rounded-lg border border-gray-200 flex-col justify-start items-start gap-5 inline-flex w-full">
      <div className="self-stretch h-14 flex-col justify-start items-start gap-2 flex">
        <div className="text-gray-700 text-lg font-semibold font-figtree leading-7">
          {t("dashboard.profile.key5")}
        </div>
        <div className="text-gray-700 text-sm font-normal font-figtree leading-tight">
          {t("dashboard.profile.key6")}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap w-full">
        <div className="justify-start items-start flex w-full lg:w-auto min-w-0">
          <div className="lg:w-[318px] w-full min-w-0 flex-col justify-start items-start inline-flex">
            <div className="self-stretch px-3 py-2 bg-white rounded-md shadow border border-gray-300 justify-start items-center inline-flex overflow-hidden min-w-0">
              <div
                className="text-gray-700 text-sm font-normal font-figtree leading-tight overflow-hidden text-ellipsis whitespace-nowrap w-full"
                title={isSuccess ? data?.user_email : ""}
              >
                {isSuccess && data?.user_email}
              </div>
            </div>
          </div>
        </div>

        <CustomButton
          variant="primary"
          type="submit"
          onClick={() => setShow(true)}
          title={t("dashboard.profile.key7")}
        />
      </div>

      <ModalShell
        show={show}
        onClose={() => setShow(false)}
        size={"md"}
        title={t("dashboard.profile.key7")}
        onSubmit={handleSubmit(onSubmit)}
        footer={
          <>
            <CustomButton
              onClick={() => setShow(false)}
              title={t("common.cancel")}
            />
            <CustomButton
              type="submit"
              variant="primary"
              disabled={!isResendRequest && isLoading}
              isLoading={!isResendRequest && isLoading}
              title={t("common.update")}
            />
          </>
        }
      >
        <div>
          <div className="mb-5">
            <CustomInput
              name="user_email"
              label={
                t("dashboard.userProfile.changeEmailForm.label-01") as string
              }
              className="shadow"
              control={control}
              type="email"
              error={errors.user_email}
              disabled
              helperText={shownFieldError(t, errors.user_email?.message)}
            />
          </div>

          <div className="mb-5">
            <CustomInput
              name="new_user_email"
              label={
                t("dashboard.userProfile.changeEmailForm.label-02") as string
              }
              className="shadow"
              error={errors.new_user_email}
              helperText={shownFieldError(t, errors.new_user_email?.message)}
              type="email"
              control={control}
            />
          </div>

          <div className="mb-5">
            <CustomInput
              name="new_user_email_confirmation"
              control={control}
              label={
                t("dashboard.userProfile.changeEmailForm.label-03") as string
              }
              className="shadow"
              type="email"
              error={errors.new_user_email_confirmation}
              helperText={shownFieldError(
                t,
                errors.new_user_email_confirmation?.message
              )}
            />
          </div>

          <CustomButton
            className="w-full"
            onClick={handleResendValidationLink}
            disabled={showTimer || isResending}
            isLoading={isResending}
            comp={showTimer && <Timer setShowTimer={setShowTimer} ttl={120} />}
            title={t("dashboard.userProfile.changeEmailForm.btn-02")}
          />
        </div>
      </ModalShell>
    </div>
  );
};

export default ProfileEmailChange;
